import { Button, Chip } from "@heroui/react";
import HintTooltip from "@/components/common/HintTooltip";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { Trash2 } from "lucide-react";

import EditableTextCell from "@/components/common/EditableTextCell";
import { formatAmount } from "@/lib/format";
import {
  PROCESS_CATEGORY_LABEL,
  PROCESS_CATEGORY_OPTIONS,
  type Process,
  type ProcessCategory,
} from "./api";

/**
 * 工程一覧の列定義。**ヘッダとセルの単一ソース**。
 *
 * 取引先マスタ（[[buildPartnerColumns]]）と同じ作りで、セルをクリックして
 * 1 項目だけ編集する。編集ダイアログの開閉は一覧側が持つ（セルごとに Modal を
 * 持たせると行数 × 列数ぶんの Modal が生まれるため）。
 */

/**
 * 費目ごとの色。客先の回答欄の並び（材料費 → 機械加工費 → 製缶費 → 処理費 →
 * その他）を流し見して区別できるよう、隣り合う費目に違う色を当てている。
 */
const PROCESS_CATEGORY_COLOR: Record<
  ProcessCategory,
  "default" | "primary" | "secondary" | "success" | "warning"
> = {
  material: "warning",
  machining: "primary",
  fabrication: "secondary",
  treatment: "success",
  other: "default",
};

/** クリックで編集できる項目。 */
export interface ProcessField {
  key: keyof Process & string;
  label: string;
  size: number;
  required?: boolean;
  /** 入力の種類。既定はテキスト。 */
  kind?: "text" | "number" | "select";
  /** kind が "select" のときの選択肢。 */
  options?: { key: string; label: string }[];
  /** 登録フォームの初期値（kind が "select" の必須項目で使う）。 */
  defaultValue?: string;
  /** 編集ダイアログの補足。 */
  description?: string;
  /** API に渡す並び替えキー。未指定なら並び替え不可。 */
  sortKey?: string;
  /** 常に表示し、非表示にできない列。 */
  mandatory?: boolean;
  /** 左に固定する列。 */
  pinned?: boolean;
}

export const PROCESS_FIELDS: ProcessField[] = [
  {
    key: "process_code",
    label: "工程コード",
    size: 150,
    required: true,
    sortKey: "process_code",
    mandatory: true,
    pinned: true,
  },
  {
    key: "process_name",
    label: "工程名",
    size: 220,
    required: true,
    sortKey: "process_name",
  },
  {
    key: "category",
    label: "費目",
    size: 140,
    // NOT NULL なので未設定に戻せない（必ずどれかの費目に属する）。
    required: true,
    kind: "select",
    options: PROCESS_CATEGORY_OPTIONS,
    defaultValue: "other",
    description:
      "客先への回答は費目別の内訳（材料費・機械加工費・製缶費・処理費・その他）で求められます。この工程の金額をどの費目として集計するかを選んでください。",
    sortKey: "category",
  },
  {
    key: "base_price",
    label: "時間単価",
    size: 150,
    kind: "number",
    description:
      "見積で時間を入れたときの既定値です。空にすると、この工程は金額を直接入力して見積ります。変更しても過去の見積金額は変わりません。",
    sortKey: "base_price",
  },
];

export interface ProcessColumnHandlers {
  /** 工程を編集できるか（マスタ編集は ADMIN のみ）。 */
  canWrite: boolean;
  onEditField: (process: Process, field: ProcessField) => void;
  onToggleDisabled: (process: Process) => void;
  onDelete: (process: Process) => void;
}

const helper = createColumnHelper<Process>();

/** ISO 日時を「YYYY-MM-DD」で表示する（一覧では時刻まで要らない）。 */
function formatDate(value: string | null): string {
  return value ? value.slice(0, 10) : "—";
}

export function buildProcessColumns({
  canWrite,
  onEditField,
  onToggleDisabled,
  onDelete,
}: ProcessColumnHandlers): ColumnDef<Process, never>[] {
  const fieldColumns = PROCESS_FIELDS.map((field) =>
    helper.accessor((row) => row[field.key], {
      id: field.key,
      header: field.label,
      size: field.size,
      meta: {
        label: field.label,
        mandatory: field.mandatory,
        pinned: field.pinned,
        sortKey: field.sortKey,
        numeric: field.kind === "number",
      },
      cell: (ctx) => {
        const value = ctx.getValue() as string | null;

        // 選択肢から選ぶ項目は、自由入力のセル（下線付きリンク）ではなく
        // 使用状態と同じ Chip で見せる。下線が付くと手で打てるように見える。
        if (field.kind === "select") {
          const category = value as ProcessCategory;
          const chip = (
            <Chip size="sm" variant="flat" color={PROCESS_CATEGORY_COLOR[category]}>
              {PROCESS_CATEGORY_LABEL[category]}
            </Chip>
          );
          if (!canWrite) return chip;
          return (
            <button
              type="button"
              className="cursor-pointer"
              onClick={() => onEditField(ctx.row.original, field)}
              aria-label={`${field.label}を編集`}
            >
              {chip}
            </button>
          );
        }

        return (
          <EditableTextCell
            label={field.label}
            // 金額は 3 桁区切りで見せる（編集ダイアログでは生の値を扱う）。
            value={field.kind === "number" && value ? formatAmount(value) : value}
            canWrite={canWrite}
            onEdit={() => onEditField(ctx.row.original, field)}
          />
        );
      },
    }),
  );

  // 使用停止。工程は削除せずここで候補から外す運用にする（過去の見積の内訳が
  // 消えて金額が変わるのを防ぐため、使用中の工程は削除もできない）。
  const disabledColumn = helper.accessor("disabled", {
    id: "disabled",
    header: "使用",
    size: 110,
    meta: { label: "使用状態" },
    cell: (ctx) => {
      const stopped = ctx.getValue();
      const chip = (
        <Chip size="sm" variant="flat" color={stopped ? "danger" : "success"}>
          {stopped ? "使用停止" : "使用中"}
        </Chip>
      );
      if (!canWrite) return chip;
      return (
        <button
          type="button"
          className="cursor-pointer"
          onClick={() => onToggleDisabled(ctx.row.original)}
          aria-label={stopped ? "使用を再開する" : "使用を停止する"}
        >
          {chip}
        </button>
      );
    },
  });

  const timestampColumns = [
    helper.accessor("created_at", {
      id: "created_at",
      header: "作成日",
      size: 120,
      meta: { label: "作成日", sortKey: "created_at" },
      cell: (ctx) => formatDate(ctx.getValue()),
    }),
    helper.accessor("updated_at", {
      id: "updated_at",
      header: "更新日",
      size: 120,
      meta: { label: "更新日" },
      cell: (ctx) => formatDate(ctx.getValue()),
    }),
  ];

  const actionsColumn = helper.display({
    id: "actions",
    header: "操作",
    size: 80,
    enableResizing: false,
    meta: { label: "操作", mandatory: true },
    cell: (ctx) => (
      <HintTooltip content={canWrite ? "削除" : "管理者の権限が必要です"}>
        {/* 無効なボタンはイベントを出さないため、Tooltip 用に包む。 */}
        <span className="inline-flex">
          <Button
            isIconOnly
            size="sm"
            variant="light"
            color="danger"
            aria-label={`${ctx.row.original.process_name} を削除`}
            isDisabled={!canWrite}
            onPress={() => onDelete(ctx.row.original)}
          >
            <Trash2 size={16} />
          </Button>
        </span>
      </HintTooltip>
    ),
  });

  // 使用状態は普段そう見ない項目なので、操作列の直前（右端）に置く。
  return [
    ...fieldColumns,
    ...timestampColumns,
    disabledColumn,
    actionsColumn,
  ] as ColumnDef<Process, never>[];
}

/** 列 id の一覧（設定の永続化で「未知のキー」を捨てるのに使う）。表示順と一致させる。 */
export const processColumnIds = [
  ...PROCESS_FIELDS.map((f) => f.key),
  "created_at",
  "updated_at",
  "disabled",
  "actions",
];

/** 左に固定する列の id。 */
export const processPinnedColumnIds = PROCESS_FIELDS.filter((f) => f.pinned).map(
  (f) => f.key,
);
